// Centralized rules for URLs that should NOT render rich link preview cards.
// Keep this list focused on immutable patterns that are known to fail or to
// produce misleading previews. Tweets containing these links should keep the
// URL inline in the text.

export interface PreviewExclusionRule {
  // Hostnames to match. A rule matches if the URL's hostname equals or ends
  // with one of these values (allowing subdomains).
  hosts: readonly string[];
  // Optional path prefixes (e.g. "/i/broadcasts"). If provided, at least one
  // prefix must match the start of the normalized pathname.
  pathPrefixes?: readonly string[];
}

const WWW_PREFIX_PATTERN = /^www\./i;

const normalizeHostname = (host: string): string => host.replace(WWW_PREFIX_PATTERN, '').toLowerCase();
const normalizePathname = (path: string): string => {
  if (!path) return '/';
  const p = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  return p;
};

// Initial set of exclusions.
// - X Live Broadcasts cannot be previewed reliably via OpenGraph; keep inline.
export const PREVIEW_EXCLUSION_RULES: readonly PreviewExclusionRule[] = [
  {
    hosts: ['x.com', 'twitter.com'],
    pathPrefixes: ['/i/broadcasts'],
  },
];

// Check whether a given URL should be excluded from rich previews.
export function isUrlExcludedFromLinkPreview(rawUrl: string): boolean {
  let url: URL | null = null;
  try {
    url = new URL(rawUrl);
  } catch {
    return false;
  }

  const host = normalizeHostname(url.hostname);
  const path = normalizePathname(url.pathname);

  // Host matches if equal or subdomain of a listed host
  const hostMatches = (ruleHost: string): boolean => {
    const r = normalizeHostname(ruleHost);
    return host === r || host.endsWith(`.${r}`);
  };

  for (const rule of PREVIEW_EXCLUSION_RULES) {
    if (!rule.hosts.some((ruleHost) => hostMatches(ruleHost))) continue;
    if (rule.pathPrefixes && rule.pathPrefixes.length > 0) {
      if (rule.pathPrefixes.some((p) => path.startsWith(p))) return true;
      continue;
    }
    return true; // Host-only rule
  }

  return false;
}
